import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, OnInit, signal, untracked } from '@angular/core'
import { InsuredSidebarStateService } from '@app/layout/sidebar/services/insured-sidebar-state.service'
import { InsuredCertificatesStateService } from '@app/layout/sidebar/services/insured-certificates-state.service'
import { ListViewInsuredCodeListItemComponent } from './list-view-insured-code-list-item/list-view-insured-code-list-item.component'
import { ListViewInsuredCertificateItemComponent } from './list-view-insured-certificate-item/list-view-insured-certificate-item.component'
import { ScrollerModule } from 'primeng/scroller'
import { SkeletonModule } from 'primeng/skeleton'
import { CommonModule } from '@angular/common'
import { LazyLoadEvent } from 'primeng/api'
import { CertificateNavigationItem } from '@app/layout/sidebar/models/certificate-navigation-item.model'
import { certificateListName } from './certificate-name'
import { InsuredNavigationItem } from '@app/layout/sidebar/models/insured-navigation-item.model'
import {
  BULK_CERTIFICATE_ACTIONS,
  CERTIFICATE_OPTION_ACTIONS,
  CertificateAction,
  SEND_DOCUMENT_ACTION,
  SEND_DOCUMENTS_ACTION,
  SHOW_PREVIEW_ACTION
} from './certificate-actions'
import { certificateIndexOfRow, CertificateRow, groupCertificatesByObjectType, toCertificateRows } from './certificate-rows'
import { toSignal } from '@angular/core/rxjs-interop'
import { CategoryService } from '@app/core/auth/services/category.service'
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router'
import { filter, map, startWith } from 'rxjs'
import { CertificateCategory } from '@app/shared/models/certificate-category.model'
import { ItemSnappedHeightDirective } from '@directives/item-snapped-height.directive'
import { DocumentPreviewService } from '@features/certificate-preview/services/document-preview.service'

@Component({
  selector: 'albert-listview-insured-certificates',
  standalone: true,
  imports: [
    CommonModule,
    ScrollerModule,
    SkeletonModule,
    ListViewInsuredCodeListItemComponent,
    ListViewInsuredCertificateItemComponent,
    ItemSnappedHeightDirective
  ],
  templateUrl: './list-view-insured-certificates.component.html',
  styleUrl: './list-view-insured-certificates.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListViewInsuredCertificatesComponent implements OnInit {
  public readonly insuredUuid = input<string | null>(null)
  public readonly sidebarDrawerVisible = model(false)
  public readonly insuredItemSize = 58
  public readonly certificateItemSize = 58
  public readonly pageSize = 20

  public readonly insuredSidebarState = inject(InsuredSidebarStateService)
  public readonly insuredCertificatesState = inject(InsuredCertificatesStateService)

  public readonly insuredItems$ = this.insuredSidebarState.insuredList$
  public readonly insuredLoading$ = this.insuredSidebarState.loading$
  public readonly certificateLoading$ = this.insuredCertificatesState.loading$

  private readonly categoryService = inject(CategoryService)
  private readonly certificateItems = toSignal(this.insuredCertificatesState.certificateList$, { initialValue: [] })
  private readonly router = inject(Router)
  private readonly documentPreviewService = inject(DocumentPreviewService)

  /** The certificate open in the detail view, if any. */
  private readonly openCertificateUuid = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      startWith(null),
      map(() => this.certificateUuidFromRoute())
    ),
    { initialValue: null }
  )

  /**
   * Lease certificates are shown grouped into their object type subsections; every other category
   * has a single object type, so its list stays flat.
   */
  public readonly certificateRows = computed<CertificateRow[]>(() => {
    const items = this.certificateItems()
    return this.categoryService.activeCertificateCategory() === CertificateCategory.LEASE
      ? groupCertificatesByObjectType(items)
      : toCertificateRows(items)
  })

  public readonly selectedUuids = signal<ReadonlySet<string>>(new Set())
  /**
   * Once several certificates are selected, single-certificate options are off for every row and
   * right-click on a selected row offers the bulk actions instead.
   */
  public readonly multipleSelected = computed(() => this.selectedUuids().size > 1)

  /** Subtitle of the open Options panel, or null while the certificate list is shown. */
  public readonly optionsSubtitle = signal<string | null>(null)
  public readonly isBulkOptions = signal(false)
  /** The certificate a single-certificate Options panel was opened for. */
  public readonly optionsCertificate = signal<CertificateNavigationItem | null>(null)

  public readonly optionsInsured = signal<InsuredNavigationItem | null>(null)

  private readonly selectedCertificates = computed<CertificateNavigationItem[]>(() => {
    const selected = this.selectedUuids()
    return this.certificateItems()
      .filter((item): item is CertificateNavigationItem => item !== undefined)
      .filter(item => selected.has(item.uuid))
  })

  private readonly canSendDocuments = computed(() => {
    if (this.optionsInsured() !== null) {
      return true
    }
    return this.selectedCertificates().some(item => item.documentPresence)
  })

  public readonly optionsActions = computed<readonly CertificateAction[]>(() => {
    if (this.isBulkOptions()) {
      const canSend = this.canSendDocuments()
      return BULK_CERTIFICATE_ACTIONS.map(action => 
        action.testId === SEND_DOCUMENTS_ACTION ? { ...action, disabled: !canSend } : action
      )
    }

    const selectedCert = this.optionsCertificate()
    const canSend = selectedCert?.documentPresence === true
    
    const isLeaseCategory = selectedCert?.certificateCategory === CertificateCategory.LEASE 
      || this.categoryService.activeCertificateCategory() === CertificateCategory.LEASE

    return CERTIFICATE_OPTION_ACTIONS
      .filter(action => 
        action.testId !== 'certificate-option-restricted-countries' || isLeaseCategory
      )
      .map(action => {
        if (action.testId === SEND_DOCUMENT_ACTION) {
          return { ...action, disabled: !canSend }
        }
        return action
      })
  })

  constructor() {
    effect(() => {
      const insuredUuid = this.insuredUuid()
      untracked(() => {
        this.selectedUuids.set(new Set())
        this.closeOptions()
        this.scopeCertificatesTo(insuredUuid)
      })
    })
  }

  public ngOnInit(): void {
    this.insuredSidebarState.ensureLoaded(this.pageSize).subscribe()
  }

  public onInsuredLazyLoad(event: LazyLoadEvent): void {
    const last = event.last ?? 0
    const page = Math.floor(last / this.pageSize)
    this.insuredSidebarState.loadMoreInsuredItems(page, this.pageSize).subscribe()
  }

  public onCertificateLazyLoad(event: LazyLoadEvent): void {
    // Rows and certificates drift apart once subsection headers are interleaved, so page off the
    // certificate index rather than the row index.
    const lastCertificate = certificateIndexOfRow(this.certificateRows(), event.last ?? 0)
    const page = Math.floor(lastCertificate / this.pageSize)
    this.insuredCertificatesState.loadMoreItems(page, this.pageSize).subscribe()
  }

  public closeSidebarDrawer(): void {
    this.sidebarDrawerVisible.set(false)
  }

  public toggleCertificateSelection(uuid: string): void {
    const next = new Set(this.selectedUuids())
    // The open certificate was picked by opening it, so starting a selection from it takes it along.
    const openUuid = this.openCertificateUuid()
    if (next.size === 0 && openUuid !== null && openUuid !== uuid && this.isListed(openUuid)) {
      next.add(openUuid)
    }
    if (next.has(uuid)) {
      next.delete(uuid)
    } else {
      next.add(uuid)
    }
    this.selectedUuids.set(next)
  }

  /**
   * Adds a whole subsection to the selection, or removes it again when all of its certificates are
   * already selected. Additive, so subsections can be combined into one selection.
   */
  public toggleSubsectionSelection(uuids: readonly string[]): void {
    if (uuids.length === 0) {
      return
    }
    const next = new Set(this.selectedUuids())
    const allSelected = uuids.every(uuid => next.has(uuid))
    uuids.forEach(uuid => allSelected ? next.delete(uuid) : next.add(uuid))
    this.selectedUuids.set(next)
  }

  public isSubsectionSelected(uuids: readonly string[]): boolean {
    const selected = this.selectedUuids()
    return uuids.length > 0 && uuids.every(uuid => selected.has(uuid))
  }

  public clearSelection(): void {
    this.selectedUuids.set(new Set())
  }

  public onCertificateClicked(): void {
    this.clearSelection()
    this.closeOptions()
    this.closeSidebarDrawer()
  }

  public openCertificateOptions(item: CertificateNavigationItem): void {
    this.isBulkOptions.set(false)
    this.optionsCertificate.set(item)
    this.optionsInsured.set(null)
    this.optionsSubtitle.set(certificateListName(item))
  }

  public openBulkOptions(): void {
    this.isBulkOptions.set(true)
    this.optionsCertificate.set(null)
    this.optionsInsured.set(null)
    this.optionsSubtitle.set('Bulk Action')
  }

  public openInsuredBulkOptions(item: InsuredNavigationItem): void {
    this.isBulkOptions.set(true)
    this.optionsCertificate.set(null)
    this.optionsInsured.set(item)
    this.optionsSubtitle.set(`${item.companyCode} / Bulk Action`)
  }

  public onOptionsAction(action: CertificateAction): void {
    const certificate = this.optionsCertificate()
    if (action.testId === SHOW_PREVIEW_ACTION && certificate !== null) {
      this.documentPreviewService.openPreview(certificate.uuid)
    }
  }

  public closeOptions(): void {
    this.optionsSubtitle.set(null)
    this.isBulkOptions.set(false)
    this.optionsCertificate.set(null)
    this.optionsInsured.set(null)
  }

  private isListed(uuid: string): boolean {
    return this.certificateItems().some(item => item?.uuid === uuid)
  }

  private certificateUuidFromRoute(): string | null {
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root
    while (route) {
      if (route.routeConfig?.path === 'certificate/:uuid') {
        return route.paramMap.get('uuid')
      }
      route = route.firstChild
    }
    return null
  }

  private scopeCertificatesTo(insuredUuid: string | null): void {
    const filter = insuredUuid !== null ? { insured: insuredUuid } : {}
    if (JSON.stringify(this.insuredCertificatesState.currentFilter) === JSON.stringify(filter)) {
      this.insuredCertificatesState.ensureLoaded(this.pageSize).subscribe()
      return
    }
    this.insuredCertificatesState.setFilter(filter)
  }
}
